import React from "react";
import {
  Coffee,
  Heart,
  MapPin,
  Mountain,
  Quote,
  Sparkles,
  Star,
  Waves,
} from "lucide-react";
import "./KilcarLodge.css";

const img = (id, w = 1400, h = 1000) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&h=${h}&q=82`;

const rooms = [
  {
    title: "Atlantic View Room",
    text: "A quiet, elegant room with soft linens, a private bathroom, and views that settle the pace of the day.",
    image: img("photo-1505693416388-ac5ce068fe85"),
    details: ["Peaceful views", "Private bathroom", "Soft bedding"],
  },
  {
    title: "Garden Double",
    text: "Warm ivory tones, countryside calm, and a restful place to return after the cliffs, beaches, and village roads.",
    image: img("photo-1618220179428-22790b461013"),
    details: ["Country garden outlook", "Tea corner", "Relaxed atmosphere"],
  },
  {
    title: "Family Guest Room",
    text: "Simple comfort with space to unwind, made for slow mornings and evenings by the Wild Atlantic Way.",
    image: img("photo-1560185007-c5ca9d2c014d"),
    details: ["Family friendly", "Comfortable beds", "Warm natural light"],
  },
];

const attractions = [
  {
    title: "Slieve League Cliffs",
    image: img("photo-1586996292898-71f4036c4e07", 900, 900),
    text: "Dramatic cliff walks and Atlantic views close to Kilcar.",
  },
  {
    title: "Wild Atlantic Way",
    image: img("photo-1500530855697-b586d89ba3ee", 900, 900),
    text: "Scenic drives through Donegal's rugged coast and open skies.",
  },
  {
    title: "Coastal Walks",
    image: img("photo-1507525428034-b723cf961d3e", 900, 900),
    text: "Salt air, stone walls, quiet lanes, and soft sea light.",
  },
  {
    title: "Local Villages",
    image: img("photo-1494526585095-c41746248156", 900, 900),
    text: "Traditional pubs, craft, music, and warm Donegal character.",
  },
];

const reviews = [
  {
    quote:
      "It felt less like checking into accommodation and more like being welcomed into a beautiful Donegal home.",
    name: "Aoife and Mark",
    meta: "Weekend escape",
  },
  {
    quote:
      "Misty mornings, a perfect breakfast, and the kindest hosts. Kilcar Lodge was exactly the calm we needed.",
    name: "Claire",
    meta: "Atlantic road trip",
  },
  {
    quote:
      "The rooms were peaceful, the coastline was unforgettable, and every detail felt personal.",
    name: "James and Niamh",
    meta: "Anniversary stay",
  },
];

const navItems = ["Welcome", "Rooms", "Breakfast", "Explore", "Reviews"];

export default function KilcarLodge() {
  return (
    <main className="kilcar-page">
      <nav className="kilcar-nav" aria-label="Kilcar Lodge navigation">
        <a className="kilcar-brand" href="#top" aria-label="Kilcar Lodge home">
          <span>KL</span>
          <strong>Kilcar Lodge</strong>
        </a>
        <div className="kilcar-nav-links">
          {navItems.map((item) => (
            <a key={item} href={`#${item.toLowerCase()}`}>
              {item}
            </a>
          ))}
        </div>
        <a className="kilcar-nav-cta" href="#booking">
          Book Your Stay
        </a>
      </nav>

      <section id="top" className="kilcar-hero">
        <div className="kilcar-hero-bg" />
        <div className="kilcar-hero-overlay" />
        <div className="kilcar-hero-content">
          <p className="kilcar-kicker">
            <MapPin size={16} />
            County Donegal, Ireland
          </p>
          <h1>A quiet romantic escape by the Wild Atlantic Way</h1>
          <p>
            Kilcar Lodge is a family-run bed and breakfast shaped by coastal
            light, green hills, warm hospitality, and the slow beauty of the
            Donegal peninsula.
          </p>
          <div className="kilcar-hero-actions">
            <a className="kilcar-button kilcar-button-primary" href="#booking">
              Book Your Stay
            </a>
            <a className="kilcar-button kilcar-button-secondary" href="#welcome">
              Explore Kilcar Lodge
            </a>
          </div>
        </div>
        <div className="kilcar-hero-note" aria-label="Lodge highlights">
          <Sparkles size={18} />
          <span>Family-run hospitality, coastal calm, unforgettable mornings.</span>
        </div>
      </section>

      <section id="welcome" className="kilcar-section kilcar-welcome">
        <div className="kilcar-section-copy">
          <p className="kilcar-eyebrow">Welcome</p>
          <h2>A family-run stay with time, warmth, and personal care</h2>
          <p>
            Set near the dramatic Donegal coastline, Kilcar Lodge is designed
            for guests who want more than a place to sleep. It is a softer kind
            of retreat: calm rooms, thoughtful hosts, quiet roads, and a feeling
            of being looked after from the moment you arrive.
          </p>
          <div className="kilcar-signature">
            <span />
            <p>Peaceful countryside. Atlantic weather. Irish welcome.</p>
          </div>
        </div>
        <div className="kilcar-editorial-image">
          <img
            src={img("photo-1441974231531-c6227db76b6e", 1200, 1400)}
            alt="Misty green countryside near the Atlantic coast"
          />
        </div>
      </section>

      <section id="rooms" className="kilcar-section kilcar-muted">
        <div className="kilcar-section-header">
          <p className="kilcar-eyebrow">Accommodation</p>
          <h2>Rooms made for quiet evenings and slow mornings</h2>
          <p>
            Each room is imagined with soft textures, peaceful views, private
            comfort, and the understated elegance of a countryside lodge.
          </p>
        </div>
        <div className="kilcar-room-grid">
          {rooms.map((room) => (
            <article className="kilcar-room-card" key={room.title}>
              <img src={room.image} alt={`${room.title} at Kilcar Lodge`} />
              <div>
                <h3>{room.title}</h3>
                <p>{room.text}</p>
                <ul>
                  {room.details.map((detail) => (
                    <li key={detail}>{detail}</li>
                  ))}
                </ul>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="breakfast" className="kilcar-section kilcar-breakfast">
        <div className="kilcar-breakfast-image">
          <img
            src={img("photo-1533089860892-a7c6f0a88666", 1200, 1000)}
            alt="Warm breakfast table with coffee and fresh morning food"
          />
        </div>
        <div className="kilcar-breakfast-panel">
          <p className="kilcar-eyebrow">Breakfast</p>
          <h2>The morning is part of the memory</h2>
          <p>
            Begin with the comfort of a generous Irish breakfast, local produce,
            homemade touches, fresh tea and coffee, and a room filled with soft
            morning light before the coast calls you out.
          </p>
          <div className="kilcar-breakfast-list">
            <span>
              <Coffee size={18} />
              Fresh tea and coffee
            </span>
            <span>
              <Heart size={18} />
              Homemade touches
            </span>
            <span>
              <Star size={18} />
              Local produce
            </span>
          </div>
        </div>
      </section>

      <section id="explore" className="kilcar-section kilcar-explore">
        <div className="kilcar-section-header">
          <p className="kilcar-eyebrow">Explore Donegal</p>
          <h2>Cliffs, coast roads, beaches, villages, and Atlantic air</h2>
        </div>
        <div className="kilcar-attraction-grid">
          {attractions.map((item, index) => (
            <article className="kilcar-attraction" key={item.title}>
              <img src={item.image} alt={item.title} />
              <div>
                {index % 2 === 0 ? <Waves size={20} /> : <Mountain size={20} />}
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="reviews" className="kilcar-section kilcar-reviews">
        <div className="kilcar-section-header">
          <p className="kilcar-eyebrow">Guest Words</p>
          <h2>Personal, peaceful, and warmly remembered</h2>
        </div>
        <div className="kilcar-review-grid">
          {reviews.map((review) => (
            <article className="kilcar-review-card" key={review.name}>
              <Quote size={26} />
              <p>{review.quote}</p>
              <div>
                <strong>{review.name}</strong>
                <span>{review.meta}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section id="booking" className="kilcar-final-cta">
        <div className="kilcar-final-overlay" />
        <div className="kilcar-final-content">
          <p className="kilcar-eyebrow">Plan Your Stay</p>
          <h2>Let Donegal slow everything down</h2>
          <p>
            Come for the cliffs and ocean roads. Stay for the quiet, the
            breakfast, the soft evening light, and the feeling of being far from
            the rush.
          </p>
          <a className="kilcar-button kilcar-button-primary" href="mailto:hello@kilcarlodge.example">
            Plan Your Escape
          </a>
        </div>
      </section>

      <footer className="kilcar-footer">
        <div>
          <h2>Kilcar Lodge</h2>
          <p>Family-run bed and breakfast near the Wild Atlantic Way.</p>
        </div>
        <div className="kilcar-footer-links">
          <a href="#welcome">Welcome</a>
          <a href="#rooms">Rooms</a>
          <a href="#breakfast">Breakfast</a>
          <a href="#explore">Explore</a>
        </div>
        <div className="kilcar-contact">
          <span>County Donegal, Ireland</span>
          <span>hello@kilcarlodge.example</span>
          <span>Instagram / Facebook</span>
        </div>
      </footer>
    </main>
  );
}
